import type { MerchantDraftPermissionAnswer } from "@unbox/api";
import { Check, CheckCircle, Hand, User01 } from "@untitledui/icons";
import { type ReactNode, useState } from "react";
import { Button } from "@/components/base/buttons/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { cx } from "@/utils/cx";

const OPTIONS: { value: MerchantDraftPermissionAnswer; label: string; description: string }[] = [
  { value: "yes", label: "Yes, I can", description: "I can sign in to TikTok Shop with the Primary account." },
  { value: "no", label: "No, I can't", description: "Someone else has to approve it." },
  { value: "not_sure", label: "Not sure", description: "I don't know who can approve it yet." },
];

const CONFIRMATIONS: Record<
  MerchantDraftPermissionAnswer,
  { icon: ReactNode; title: string; body: ReactNode; className: string }
> = {
  yes: {
    icon: <CheckCircle className="size-5 text-success-600" />,
    title: "Awesome, let's start adding your merchant.",
    body: "Next, you'll approve access to your TikTok Shop and configure your risks. Hit continue when you're ready.",
    className: "border-success-200 bg-success-50 text-success-700",
  },
  no: {
    icon: <Hand className="size-5 text-secondary" />,
    title: "No problem, you can still start setting up your merchant.",
    body: "You just won't be able to finish until someone with permission has approved access. Look out for the link to share in the Connect TikTok shop step.",
    className: "border-secondary bg-unbox-cream-50 text-secondary",
  },
  not_sure: {
    icon: <User01 className="size-5 text-secondary" />,
    title: "Find out who has access to this TikTok Shop.",
    body: "You can still start setting up your merchant, but someone with permission has to approve access in TikTok Shop before you can finish.",
    className: "border-secondary bg-unbox-cream-50 text-secondary",
  },
};

export const MerchantPermissionModal = ({
  isOpen,
  onOpenChange,
  onContinue,
  isPending,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onContinue: (answer: MerchantDraftPermissionAnswer) => void;
  isPending?: boolean;
}) => {
  const [answer, setAnswer] = useState<MerchantDraftPermissionAnswer | null>(null);
  const confirmation = answer ? CONFIRMATIONS[answer] : null;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) setAnswer(null);
        onOpenChange(open);
      }}
    >
      <DialogContent className="max-w-[960px] min-h-[520px] p-0 gap-0 overflow-hidden rounded-2xl sm:rounded-2xl flex">
        <div
          className="relative hidden sm:block w-2/5 shrink-0"
          style={{ background: "linear-gradient(150deg, #2a1838 0%, #3a2252 55%, #241430 100%)" }}
          aria-hidden="true"
        >
          <img src="/white-logo.png" alt="" className="absolute top-7 left-7 h-6" />
          <img
            src="/safety/aa254be04a7ba1180f1bbe583d7b9922e26a1a8a.png"
            alt=""
            className="absolute inset-0 size-full object-cover opacity-60"
            style={{ maskImage: "linear-gradient(to bottom, transparent, white 30%, white 85%, transparent)" }}
          />
        </div>

        <div className="flex flex-1 flex-col gap-6 p-8">
          <div className="flex flex-col gap-2 pr-6">
            <DialogTitle className="text-display-xs font-semibold leading-8 text-primary text-balance">
              Do you have permission to connect existing TikTok Shops?
            </DialogTitle>
            <DialogDescription className="text-sm text-tertiary">
              Adding a merchant means approving Unbox's permissions in TikTok Shop, and only the shop's Primary account
              can do that. Without it, you won't be able to complete setup.
            </DialogDescription>
          </div>

          <div role="radiogroup" aria-label="TikTok Shop permission" className="rounded-xl border border-secondary">
            {OPTIONS.map((option) => {
              const selected = answer === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => setAnswer(option.value)}
                  className={cx(
                    "flex w-full items-center gap-4 px-4 py-3.5 text-left transition-colors duration-100 border-b border-secondary last:border-b-0 first:rounded-t-xl last:rounded-b-xl",
                    selected ? "bg-brand-25" : "hover:bg-primary_hover",
                  )}
                >
                  <span className="flex flex-1 flex-col">
                    <span className="text-sm font-semibold text-primary">{option.label}</span>
                    <span className="text-sm text-tertiary">{option.description}</span>
                  </span>
                  <span
                    className={cx(
                      "flex size-5 shrink-0 items-center justify-center rounded-full border",
                      selected ? "border-brand-950 bg-brand-950" : "border-primary bg-primary",
                    )}
                  >
                    {selected && <Check className="size-3 text-white" strokeWidth={3} />}
                  </span>
                </button>
              );
            })}
          </div>

          {confirmation && (
            <div
              role="status"
              className={cx("flex gap-3 rounded-lg border p-4 animate-in fade-in-0", confirmation.className)}
            >
              <span className="shrink-0">{confirmation.icon}</span>
              <div className="flex flex-col gap-1 text-sm">
                <p className="font-semibold">{confirmation.title}</p>
                <p>{confirmation.body}</p>
              </div>
            </div>
          )}

          <div className="mt-auto flex items-center justify-between gap-3">
            <Button color="secondary" size="lg" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              color="primary"
              size="lg"
              isDisabled={!answer}
              isLoading={isPending}
              onClick={() => answer && onContinue(answer)}
            >
              Continue
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
